import { useCallback, useMemo, useRef, useState } from "react";
import {
  loadDirectoryFacilities,
  loadDirectoryGrounds,
} from "../lib/directoryLoad";
import {
  answerKey,
  cachedAnswer,
  createViewportCache,
  shouldLoadFacilities,
  viewportKey,
  viewportTruncationNotice,
} from "../lib/mapViewport";

/**
 * Issue #643 — the directory, fetched for the VIEWPORT the map is showing.
 *
 * ## Why this is a hook file and not part of `GroundsMap.jsx`
 *
 * Two reasons, and neither is taste.
 *
 * The first is the budget. `GroundsMap.jsx`'s component function measured 687
 * of its 710 allowed lines before this issue (`frontend/eslint.config.mjs`
 * carries the ladder and the rule that a number is never raised to make a new
 * function fit). #617 hit the same wall and answered it the same way —
 * `DirectoryLegend` exists because the page was two lines under. The wiring
 * below is ~40 lines; it does not fit, so it moves out.
 *
 * The second is that this is the LAST place viewport code may live and still
 * be honest about itself. Every DECISION is one module further out, in
 * `lib/mapViewport.js`, where an ordinary test can call it: the snap, the
 * cache key, the mode, the cache's bounds, the truncation sentence. What is
 * left here is state and sequencing — the part that genuinely needs React and
 * that no unit test in this repo can reach, because nothing here mounts
 * components. Keeping the two apart is the rule #615 and #617 were both
 * bitten by, and the reason `lib/directoryLoad.js` exists at all (#608).
 *
 * ## What it does
 *
 * `onViewportChange` is handed a snapped `{ box, zoom }` by the map (see
 * `ViewportWatcher` in `GroundsMap.jsx`) every time a pan or zoom settles.
 * From there:
 *
 *   1. **The same key twice is not a request.** Two small pans inside one
 *      grid cell snap to one box, so the second one does nothing at all — no
 *      fetch, no cache read, no re-render. This is #643's second acceptance
 *      criterion and it is the whole reason the client snaps as well as the
 *      server.
 *   2. **A key already held is not a request either.** The cache is bounded
 *      twice over (`lib/mapViewport.js`), because an unbounded cache is this
 *      epic's own defect moved into the browser.
 *   3. **A late answer never overwrites a newer one.** A drag across a
 *      country can leave two reads in flight; `activeKey` is checked when
 *      each lands, so the one the user is actually looking at wins
 *      regardless of which returns first.
 *   4. **A failure changes nothing but the flag.** #608/#633's rule:
 *      `loadDirectoryGrounds` returns NO `grounds` key on rejection, and this
 *      hook writes no rows on `ok: false` — so a viewport fetch that fails
 *      leaves the pins that were working exactly where they were, and says
 *      so.
 */

/** Nothing loaded yet. Rows mode, because that is what a map at any usable zoom gets. */
const EMPTY_VIEW = {
  mode: "rows",
  grounds: [],
  cells: [],
  truncated: false,
  cap: null,
  facilities: [],
  facilitiesTruncated: false,
  facilitiesCap: null,
  directoryError: false,
  facilitiesError: false,
  /**
   * Has the directory answered AT ALL yet? #212's opening fit hangs off this
   * — see `fitReason` in `GroundsMap.jsx`. It is a one-way latch rather than
   * a count: the FIRST answer is a new question ("show me my directory"), and
   * every answer after it is the map's own movement coming back, which must
   * never move the map again.
   */
  answered: false,
  /**
   * Issue #683 — the snapped box THE LAST SUCCESSFUL ANSWER described, or
   * `null` before anything has landed. `lib/directoryEnrich.js` uses this to
   * decide whether a played/saved venue folded back onto the map (an
   * archived venue the directory read itself no longer carries) is actually
   * on screen — see `boxContainsPoint` in `lib/mapViewport.js`.
   *
   * Written only on SUCCESS, alongside `grounds`/`mode` in `applyGrounds`,
   * never on a failed read: a failed viewport fetch leaves the pins (and the
   * box they were fetched for) exactly where they were (#608/#633), so the
   * fold-in must reason from the same box the rows on screen actually answer.
   */
  box: null,
};

/**
 * `isAdmin` (#683) is passed straight to `loadDirectoryGrounds`/
 * `loadDirectoryFacilities`, which is what decides whether the request
 * carries `include_archived=1` at all (`directoryLoad.js`). It is not part
 * of any cache key: a signed-in session's role does not change under this
 * hook's lifetime, so two answers for the same viewport can never disagree
 * about who asked for them.
 */
export function useDirectoryViewport(api, isAdmin) {
  const [view, setView] = useState(EMPTY_VIEW);
  /** The viewport the page is currently showing an answer for. */
  const activeKey = useRef(null);
  /** The viewport the page last asked about, failure or not — see `retry`. */
  const activeViewport = useRef(null);
  const groundsCache = useRef(null);
  const facilitiesCache = useRef(null);
  if (groundsCache.current === null) groundsCache.current = createViewportCache();
  if (facilitiesCache.current === null) facilitiesCache.current = createViewportCache();

  /**
   * Fetch one viewport. `force` bypasses the cache, which is what Retry
   * needs: a retry that answered from cache would report success without
   * having asked anything.
   */
  const load = useCallback(
    (viewport, force) => {
      const key = viewportKey(viewport);
      if (key === null) return Promise.resolve();
      activeKey.current = key;
      activeViewport.current = viewport;

      const held = force ? undefined : cachedAnswer(groundsCache.current, viewport);
      const groundsRead = held
        ? Promise.resolve(held)
        : loadDirectoryGrounds(api, viewport, isAdmin).then((outcome) => {
            // Keyed on the mode that came back, which is the server's
            // decision and not a prediction — see `answerKey`.
            if (outcome.ok) groundsCache.current.set(answerKey(outcome.mode, viewport), outcome);
            return outcome;
          });

      return groundsRead.then((grounds) => {
        // A newer viewport has been asked for since: this answer describes
        // somewhere the user has already left, and applying it would draw
        // the wrong place.
        if (activeKey.current !== key) return undefined;
        applyGrounds(setView, grounds, viewport.box);
        if (!grounds.ok || !shouldLoadFacilities(grounds.mode)) return undefined;
        // The surfaces layer hangs off a ROW answer, so it shares that
        // answer's key: it does not depend on the zoom either.
        const facilitiesKey = answerKey("rows", viewport);
        const heldFacilities = force ? undefined : facilitiesCache.current.get(facilitiesKey);
        const facilitiesRead = heldFacilities
          ? Promise.resolve(heldFacilities)
          : loadDirectoryFacilities(api, viewport, isAdmin).then((outcome) => {
              if (outcome.ok) facilitiesCache.current.set(facilitiesKey, outcome);
              return outcome;
            });
        return facilitiesRead.then((facilities) => {
          if (activeKey.current !== key) return undefined;
          applyFacilities(setView, facilities);
          return undefined;
        });
      });
    },
    [api, isAdmin],
  );

  /**
   * The map settled somewhere. Does nothing when the snapped box and zoom are
   * the ones already on screen — the deduplication that makes a small pan
   * free. A zoom step inside one grid cell IS a new question by this test,
   * and is then answered from the cache without a request (`cachedAnswer`
   * looks for the box's row answer first, and a row answer does not depend
   * on the zoom).
   */
  const onViewportChange = useCallback(
    (viewport) => {
      const key = viewportKey(viewport);
      if (key === null || key === activeKey.current) return Promise.resolve();
      return load(viewport, false);
    },
    [load],
  );

  /**
   * #608's retry, for whichever viewport is on screen. Forced past the cache,
   * and it clears `activeKey` first so a failed read of the SAME box is
   * retried rather than deduplicated away.
   */
  const retry = useCallback(() => {
    const viewport = activeViewport.current;
    if (!viewport) return Promise.resolve();
    activeKey.current = null;
    return load(viewport, true);
  }, [load]);

  /**
   * `setGrounds`/`setFacilities` with the signatures the page already uses
   * (`setGrounds((prev) => …)`), so a ground corrected, created or rated in a
   * dialog updates in place without a refetch — the behaviour #131/#379/#601
   * rely on, unchanged by this issue.
   */
  const setGrounds = useCallback((update) => {
    setView((prev) => ({
      ...prev,
      grounds: typeof update === "function" ? update(prev.grounds) : update,
    }));
  }, []);
  const setFacilities = useCallback((update) => {
    setView((prev) => ({
      ...prev,
      facilities: typeof update === "function" ? update(prev.facilities) : update,
    }));
  }, []);

  const truncationNotice = useMemo(() => viewportTruncationNotice(view), [view]);

  return { ...view, truncationNotice, onViewportChange, retry, setGrounds, setFacilities };
}

/**
 * #633's rule, at the one place it can be broken: on `ok: false` the ONLY
 * thing written is the flag. No `grounds`, no `cells`, no `truncated` — the
 * map keeps drawing whatever it was drawing, and the page says the request
 * failed. Overwriting rows here with a fabricated empty list is precisely how
 * a 500 came to be reported to a real user as "No grounds here yet" (#608).
 */
function applyGrounds(setView, outcome, box) {
  if (!outcome.ok) {
    setView((prev) => ({ ...prev, directoryError: true }));
    return;
  }
  setView((prev) => ({
    ...prev,
    answered: true,
    directoryError: false,
    mode: outcome.mode,
    grounds: outcome.grounds,
    cells: outcome.cells,
    truncated: outcome.truncated,
    cap: outcome.cap,
    // Issue #683 — the box THIS answer describes, written alongside it so
    // the two can never disagree about which viewport the rows are for.
    box: box ?? prev.box,
    // Cell mode draws no ground markers, so it fetches no surfaces
    // (`shouldLoadFacilities`) — and holding the last zoom's surfaces while
    // showing counts would leave violet pins floating over bubbles they have
    // no venue for.
    facilities: outcome.mode === "cells" ? [] : prev.facilities,
    facilitiesTruncated: outcome.mode === "cells" ? false : prev.facilitiesTruncated,
  }));
}

/**
 * #166's deliberate asymmetry, kept: the surfaces layer DOES degrade to an
 * empty list, because a map that draws grounds without their nets is degraded
 * but honest, where one that refuses to draw grounds because their nets
 * failed has turned an extra into a dependency. #608 only made the
 * degradation say so.
 */
function applyFacilities(setView, outcome) {
  setView((prev) => ({
    ...prev,
    facilities: outcome.facilities,
    facilitiesError: !outcome.ok,
    facilitiesTruncated: outcome.truncated === true,
    facilitiesCap: outcome.cap,
  }));
}
